import React, { useState, useMemo } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Checkbox } from '@/components/ui/checkbox';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Slider } from '@/components/ui/slider';
import { Badge } from '@/components/ui/badge';
import {
  Shield, ChevronRight, ChevronLeft, ExternalLink, CheckCircle2,
  User, Mail, Send, Users, Eye, MessageSquare, UserCheck,
  Briefcase, Star, Heart, Lightbulb, HelpCircle, Building2,
  MapPin, Globe, Target, Zap, Award, Rocket, Compass, Flag,
  FolderOpen, Tag, Monitor, Smartphone
} from 'lucide-react';

const ICON_MAP = {
  'briefcase': Briefcase, 'star': Star, 'users': Users, 'eye': Eye,
  'heart': Heart, 'message-square': MessageSquare, 'user-check': UserCheck,
  'lightbulb': Lightbulb, 'help-circle': HelpCircle, 'building': Building2,
  'map-pin': MapPin, 'globe': Globe, 'target': Target, 'zap': Zap,
  'award': Award, 'shield': Shield, 'rocket': Rocket, 'compass': Compass,
  'flag': Flag, 'folder': FolderOpen, 'tag': Tag,
};

const getIconByName = (iconName) => ICON_MAP[iconName] || UserCheck;

export default function LandingPreview({ config, questions, segmentTypes, involvementType, projectName }) {
  const [viewMode, setViewMode] = useState('mobile'); // mobile | desktop
  const [step, setStep] = useState('welcome');
  const [responses, setResponses] = useState({});
  const [selectedInvolvement, setSelectedInvolvement] = useState(null);
  const [selectedSegments, setSelectedSegments] = useState({});
  const [identification, setIdentification] = useState({ first_name: '', last_name: '', email: '', wants_to_identify: false });

  const allSteps = useMemo(() => {
    const steps = ['welcome'];
    if (involvementType?.segments?.length > 0) steps.push('involvement');
    if (config?.allow_self_segmentation && segmentTypes?.length > 0) steps.push('segmentation');
    if (questions?.length > 0) steps.push('questions');
    if (config?.allow_self_identification) steps.push('identification');
    steps.push('complete');
    return steps;
  }, [config, questions, segmentTypes, involvementType]);

  const currentIndex = allSteps.indexOf(step);
  const canGoBack = currentIndex > 0;
  const canGoForward = currentIndex < allSteps.length - 1;

  const goBack = () => { if (canGoBack) setStep(allSteps[currentIndex - 1]); };
  const goForward = () => { if (canGoForward) setStep(allSteps[currentIndex + 1]); };
  const resetPreview = () => { setStep('welcome'); setResponses({}); setSelectedInvolvement(null); setSelectedSegments({}); setIdentification({ first_name: '', last_name: '', email: '', wants_to_identify: false }); };

  const renderQuestion = (question) => {
    const value = responses[question.id];
    switch (question.question_type) {
      case 'free_text':
        return <Textarea value={value || ''} onChange={(e) => setResponses(p => ({...p, [question.id]: e.target.value}))} placeholder="Enter your response..." rows={2} className="text-xs" />;
      case 'multiple_choice':
        return (
          <RadioGroup value={value || ''} onValueChange={(v) => setResponses(p => ({...p, [question.id]: v}))}>
            {question.options?.map((opt, i) => (
              <div key={i} className="flex items-center space-x-2">
                <RadioGroupItem value={opt} id={`prev-${question.id}-${i}`} />
                <Label htmlFor={`prev-${question.id}-${i}`} className="text-xs">{opt}</Label>
              </div>
            ))}
          </RadioGroup>
        );
      case 'slider':
        const cfg = question.slider_config || { min_value: 0, max_value: 10 };
        return (
          <div className="space-y-2">
            <Slider value={[value ?? cfg.min_value]} min={cfg.min_value} max={cfg.max_value} step={(cfg.max_value - cfg.min_value) / 100}
              onValueChange={([v]) => setResponses(p => ({...p, [question.id]: parseFloat(v.toFixed(2))}))} />
            <div className="flex justify-between text-[10px] text-slate-500">
              <span>{cfg.left_label || 'Low'}</span>
              <span>{cfg.right_label || 'High'}</span>
            </div>
          </div>
        );
      case 'yes_no':
        return (
          <RadioGroup value={value || ''} onValueChange={(v) => setResponses(p => ({...p, [question.id]: v}))}>
            <div className="flex gap-3">
              <div className="flex items-center space-x-1"><RadioGroupItem value="yes" id={`prev-${question.id}-y`} /><Label htmlFor={`prev-${question.id}-y`} className="text-xs">Yes</Label></div>
              <div className="flex items-center space-x-1"><RadioGroupItem value="no" id={`prev-${question.id}-n`} /><Label htmlFor={`prev-${question.id}-n`} className="text-xs">No</Label></div>
            </div>
          </RadioGroup>
        );
      case 'rating':
        return (
          <div className="flex gap-1 flex-wrap">
            {[1,2,3,4,5,6,7,8,9,10].map(n => (
              <Button key={n} type="button" variant={value === n ? 'default' : 'outline'} size="sm"
                className={`h-6 w-6 p-0 text-[10px] ${value === n ? 'bg-[#5AA2AE]' : ''}`}
                onClick={() => setResponses(p => ({...p, [question.id]: n}))}>{n}</Button>
            ))}
          </div>
        );
      case 'dropdown':
        return (
          <Select value={value || ''} onValueChange={(v) => setResponses(p => ({...p, [question.id]: v}))}>
            <SelectTrigger className="text-xs h-8"><SelectValue placeholder="Select..." /></SelectTrigger>
            <SelectContent>{question.options?.map((opt, i) => <SelectItem key={i} value={opt} className="text-xs">{opt}</SelectItem>)}</SelectContent>
          </Select>
        );
      default:
        return <Input value={value || ''} onChange={(e) => setResponses(p => ({...p, [question.id]: e.target.value}))} placeholder="Enter your response..." className="text-xs h-8" />;
    }
  };

  const isMobile = viewMode === 'mobile';
  const frameWidth = isMobile ? 375 : 768;
  const frameHeight = isMobile ? 667 : 500;

  const previewContent = (
    <div className="min-h-full bg-gradient-to-br from-slate-50 to-slate-100">
      {config?.hero_image_url && (
        <div className="h-24 bg-cover bg-center relative" style={{ backgroundImage: `url(${config.hero_image_url})` }}>
          <div className="absolute inset-0 bg-black/30" />
        </div>
      )}
      <div className={`mx-auto px-3 py-4 ${isMobile ? 'max-w-full' : 'max-w-md'}`}>
        {config?.logo_url && (
          <div className="flex justify-center mb-3">
            <img src={config.logo_url} alt="Logo" className="h-10 w-auto object-contain" />
          </div>
        )}

        {/* Welcome */}
        {step === 'welcome' && (
          <div className="space-y-3">
            <Card className="overflow-hidden">
              <CardHeader className="bg-[#5AA2AE]/5 border-b py-3 px-4">
                <CardTitle className="text-sm text-center" style={{ fontFamily: 'Manrope, sans-serif' }}>
                  {config?.welcome_title || `Welcome to ${projectName}`}
                </CardTitle>
              </CardHeader>
              <CardContent className="p-4 space-y-3">
                <p className="text-xs text-slate-600 leading-relaxed">{config?.welcome_message || 'We value your input...'}</p>
                <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-3">
                  <div className="flex items-start gap-2">
                    <Shield className="h-3.5 w-3.5 text-emerald-600 mt-0.5 flex-shrink-0" />
                    <div>
                      <p className="text-[10px] font-medium text-emerald-800">Your Privacy is Protected</p>
                      <p className="text-[10px] text-emerald-700">{config?.anonymity_message || 'Your responses are anonymous.'}</p>
                    </div>
                  </div>
                </div>
                {config?.learn_more_url && (
                  <span className="inline-flex items-center text-[#5AA2AE] text-[10px]">
                    Learn More about {projectName} <ExternalLink className="ml-1 h-3 w-3" />
                  </span>
                )}
              </CardContent>
            </Card>
            <Button className="w-full bg-[#5AA2AE] hover:bg-[#5AA2AE]/90 text-xs h-8" onClick={goForward}>
              Get Started <ChevronRight className="ml-1 h-3 w-3" />
            </Button>
          </div>
        )}

        {/* Involvement */}
        {step === 'involvement' && involvementType && (
          <div className="space-y-3">
            <Card>
              <CardHeader className="py-3 px-4">
                <CardTitle className="flex items-center gap-2 text-sm">
                  <Users className="h-4 w-4 text-[#5AA2AE]" />
                  {config?.involvement_question || 'How would you describe your involvement?'}
                </CardTitle>
              </CardHeader>
              <CardContent className="px-4 pb-4 pt-0">
                <div className="grid gap-2">
                  {involvementType.segments?.map((seg) => {
                    const Icon = getIconByName(seg.icon);
                    const sel = selectedInvolvement === seg.id;
                    return (
                      <button key={seg.id} type="button" onClick={() => setSelectedInvolvement(seg.id)}
                        className={`w-full p-3 rounded-lg border-2 text-left transition-all ${sel ? 'border-[#5AA2AE] bg-[#5AA2AE]/5' : 'border-slate-200 hover:border-[#5AA2AE]/50'}`}>
                        <div className="flex items-center gap-3">
                          <div className={`p-1.5 rounded-lg ${sel ? 'bg-[#5AA2AE] text-white' : 'bg-slate-100 text-slate-600'}`}>
                            <Icon className="h-3.5 w-3.5" />
                          </div>
                          <div className="flex-1">
                            <span className={`text-xs font-medium ${sel ? 'text-[#5AA2AE]' : 'text-slate-900'}`}>{seg.name}</span>
                            {sel && <CheckCircle2 className="h-3 w-3 text-[#5AA2AE] inline ml-1" />}
                            {seg.description && <p className="text-[10px] text-slate-500 mt-0.5">{seg.description}</p>}
                          </div>
                        </div>
                      </button>
                    );
                  })}
                </div>
              </CardContent>
            </Card>
            <Button className="w-full bg-[#5AA2AE] hover:bg-[#5AA2AE]/90 text-xs h-8" onClick={goForward}>
              Continue <ChevronRight className="ml-1 h-3 w-3" />
            </Button>
          </div>
        )}

        {/* Segmentation */}
        {step === 'segmentation' && (
          <div className="space-y-3">
            <Card>
              <CardHeader className="py-3 px-4">
                <CardTitle className="text-sm">Tell Us More About Yourself</CardTitle>
                <CardDescription className="text-[10px]">Select the options that best describe you.</CardDescription>
              </CardHeader>
              <CardContent className="px-4 pb-4 pt-0 space-y-4">
                {segmentTypes?.map((segType) => (
                  <div key={segType.id} className="space-y-1">
                    <Label className="text-xs font-medium">{segType.name}</Label>
                    <Select value={selectedSegments[segType.id] || ''} onValueChange={(v) => setSelectedSegments(p => ({...p, [segType.id]: v}))}>
                      <SelectTrigger className="text-xs h-8"><SelectValue placeholder="Select an option" /></SelectTrigger>
                      <SelectContent>{segType.segments?.map((seg) => <SelectItem key={seg.id} value={seg.id} className="text-xs">{seg.name}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                ))}
              </CardContent>
            </Card>
            <Button className="w-full bg-[#5AA2AE] hover:bg-[#5AA2AE]/90 text-xs h-8" onClick={goForward}>
              Continue <ChevronRight className="ml-1 h-3 w-3" />
            </Button>
          </div>
        )}

        {/* Questions */}
        {step === 'questions' && (
          <div className="space-y-3">
            <Card>
              <CardHeader className="py-3 px-4">
                <CardTitle className="text-sm">Share Your Feedback</CardTitle>
                <CardDescription className="text-[10px]">Please answer the following questions.</CardDescription>
              </CardHeader>
              <CardContent className="px-4 pb-4 pt-0 space-y-4">
                {questions?.map((q, i) => (
                  <div key={q.id} className="space-y-2">
                    {q.heading && <Badge variant="secondary" className="text-[10px]">{q.heading}</Badge>}
                    <Label className="text-xs font-medium block">
                      {i + 1}. {q.text}
                      {q.required && <span className="text-red-500 ml-1">*</span>}
                    </Label>
                    {renderQuestion(q)}
                  </div>
                ))}
              </CardContent>
            </Card>
            <Button className="w-full bg-[#5AA2AE] hover:bg-[#5AA2AE]/90 text-xs h-8" onClick={goForward}>
              Continue <ChevronRight className="ml-1 h-3 w-3" />
            </Button>
          </div>
        )}

        {/* Identification */}
        {step === 'identification' && (
          <div className="space-y-3">
            <Card>
              <CardHeader className="py-3 px-4">
                <CardTitle className="flex items-center gap-2 text-sm"><User className="h-4 w-4 text-[#5AA2AE]" />Would You Like to Identify Yourself?</CardTitle>
                <CardDescription className="text-[10px]">{config?.self_id_prompt || 'Optional: Provide your contact details.'}</CardDescription>
              </CardHeader>
              <CardContent className="px-4 pb-4 pt-0 space-y-3">
                <div className="flex items-start gap-2 p-3 rounded-lg border bg-slate-50">
                  <Checkbox checked={identification.wants_to_identify} onCheckedChange={(c) => setIdentification(p => ({...p, wants_to_identify: c}))} />
                  <div>
                    <Label className="text-xs font-medium cursor-pointer">Yes, I'd like to provide my contact information</Label>
                    <p className="text-[10px] text-slate-500">This allows the project team to reach out to you.</p>
                  </div>
                </div>
                {identification.wants_to_identify && (
                  <div className="space-y-3 p-3 border rounded-lg">
                    <div className="grid grid-cols-2 gap-2">
                      <div className="space-y-1">
                        <Label className="text-[10px]">First Name</Label>
                        <Input value={identification.first_name} onChange={(e) => setIdentification(p => ({...p, first_name: e.target.value}))} placeholder="John" className="text-xs h-7" />
                      </div>
                      <div className="space-y-1">
                        <Label className="text-[10px]">Last Name</Label>
                        <Input value={identification.last_name} onChange={(e) => setIdentification(p => ({...p, last_name: e.target.value}))} placeholder="Doe" className="text-xs h-7" />
                      </div>
                    </div>
                    <div className="space-y-1">
                      <Label className="text-[10px]">Email Address</Label>
                      <div className="relative">
                        <Mail className="absolute left-2 top-1/2 -translate-y-1/2 h-3 w-3 text-slate-400" />
                        <Input type="email" value={identification.email} onChange={(e) => setIdentification(p => ({...p, email: e.target.value}))} placeholder="john@example.com" className="pl-7 text-xs h-7" />
                      </div>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
            <Button className="w-full bg-[#5AA2AE] hover:bg-[#5AA2AE]/90 text-xs h-8" onClick={goForward}>
              Submit Feedback <Send className="ml-1 h-3 w-3" />
            </Button>
          </div>
        )}

        {/* Complete */}
        {step === 'complete' && (
          <Card>
            <CardContent className="py-8 text-center">
              <div className="w-12 h-12 rounded-full bg-emerald-100 flex items-center justify-center mx-auto mb-3">
                <CheckCircle2 className="h-6 w-6 text-emerald-600" />
              </div>
              <h2 className="text-sm font-bold text-slate-900 mb-2" style={{ fontFamily: 'Manrope, sans-serif' }}>Thank You!</h2>
              <p className="text-[10px] text-slate-600">Your feedback has been submitted successfully.</p>
            </CardContent>
          </Card>
        )}

        <div className="text-center text-[10px] text-slate-400 mt-4 pb-2">Pulse by ProjectAI</div>
      </div>
    </div>
  );

  return (
    <Card className="dashboard-card" data-testid="landing-preview-card">
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="flex items-center gap-2 text-base">
              <Eye className="h-4 w-4 text-[#5AA2AE]" />
              Live Preview
            </CardTitle>
            <CardDescription className="text-xs">Interactive preview of your public landing page</CardDescription>
          </div>
          {/* View Mode Toggle */}
          <div className="flex items-center gap-1 bg-slate-100 rounded-lg p-1">
            <Button variant={viewMode === 'mobile' ? 'default' : 'ghost'} size="sm"
              className={`h-7 px-2 text-xs ${viewMode === 'mobile' ? 'bg-[#5AA2AE] hover:bg-[#5AA2AE]/90' : ''}`}
              onClick={() => setViewMode('mobile')} data-testid="preview-mobile-btn">
              <Smartphone className="h-3.5 w-3.5 mr-1" /> Mobile
            </Button>
            <Button variant={viewMode === 'desktop' ? 'default' : 'ghost'} size="sm"
              className={`h-7 px-2 text-xs ${viewMode === 'desktop' ? 'bg-[#5AA2AE] hover:bg-[#5AA2AE]/90' : ''}`}
              onClick={() => setViewMode('desktop')} data-testid="preview-desktop-btn">
              <Monitor className="h-3.5 w-3.5 mr-1" /> Desktop
            </Button>
          </div>
        </div>
      </CardHeader>
      <CardContent className="pt-0">
        {/* Step Navigation */}
        <div className="flex items-center justify-between mb-3 bg-slate-50 rounded-lg p-2">
          <Button variant="ghost" size="sm" className="h-7 px-2 text-xs" onClick={goBack} disabled={!canGoBack} data-testid="preview-back-btn">
            <ChevronLeft className="h-3.5 w-3.5 mr-1" /> Back
          </Button>
          <div className="flex items-center gap-1.5">
            {allSteps.map((s, i) => (
              <button key={s} onClick={() => setStep(s)}
                className={`w-2 h-2 rounded-full transition-all ${i === currentIndex ? 'bg-[#5AA2AE] w-4' : i < currentIndex ? 'bg-[#5AA2AE]/40' : 'bg-slate-300'}`}
                data-testid={`preview-dot-${s}`} />
            ))}
          </div>
          <div className="flex items-center gap-1">
            <Badge variant="outline" className="text-[10px] capitalize">{step.replace('_', ' ')}</Badge>
            <Button variant="ghost" size="sm" className="h-7 px-2 text-xs" onClick={goForward} disabled={!canGoForward} data-testid="preview-forward-btn">
              Next <ChevronRight className="h-3.5 w-3.5 ml-1" />
            </Button>
          </div>
        </div>

        {/* Device Frame */}
        <div className="flex justify-center">
          <div className={`relative transition-all duration-300 ${isMobile ? 'w-[375px]' : 'w-full max-w-[768px]'}`}>
            {/* Device Chrome */}
            <div className={`rounded-2xl border-2 border-slate-300 bg-white overflow-hidden shadow-lg ${isMobile ? 'rounded-[2rem]' : 'rounded-xl'}`}>
              {/* Status Bar */}
              <div className="bg-slate-800 px-4 py-1.5 flex items-center justify-between">
                <span className="text-white text-[10px] font-medium">9:41</span>
                <div className={`h-4 ${isMobile ? 'w-20' : 'w-0'} bg-slate-700 rounded-full`} />
                <div className="flex items-center gap-1">
                  <div className="w-3 h-2 border border-white rounded-sm"><div className="w-1.5 h-full bg-white rounded-sm" /></div>
                </div>
              </div>
              {/* URL Bar */}
              <div className="bg-slate-100 px-3 py-1.5 border-b">
                <div className="bg-white rounded-full px-3 py-1 text-[10px] text-slate-500 text-center truncate">
                  pulse.projectai.com/public/{projectName?.toLowerCase().replace(/\s+/g, '-') || 'project'}
                </div>
              </div>
              {/* Content */}
              <div className="overflow-y-auto" style={{ height: frameHeight }}>
                {previewContent}
              </div>
            </div>
          </div>
        </div>

        {/* Reset Button */}
        <div className="flex justify-center mt-3">
          <Button variant="outline" size="sm" className="text-xs h-7" onClick={resetPreview} data-testid="preview-reset-btn">
            Reset Preview
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}
